export type Coupon = {
  applicationContext?: ApplicationContext;
  situation?: InsuranceSituation;
  carData?: CarData;
  carUsageData?: CarUsageData;
  financingData?: FinancingData;
  policyholder?: PolicyHolder;
  partner?: PartnerInformation;
  vehicleOwner?: VehicleOwner;
  driverData?: DriverData;
  drivingExperienceData?: DrivingExperienceData;
  previousInsuranceData?: PreviousInsuranceData;
  insuranceData?: InsuranceData;
  payment?: Payment;
  cover?: Cover;
  externalReferences?: ExternalReferences;
};

export type ApplicationContext = {
  ratingDate?: string;
  marketingId?: string;
  // Possible values?
  cooperationId?: string;
  // Possible values?
  usecase?: Usecase;
  // Type? As far as we know it can only be null
  couponReference?: string | null;
};

export type CarData = {
  manufacturingYear?: number;
  hsn?: string;
  tsn?: string;
  purchaseYear?: number;
  parkDistanceControlSystem?: boolean;
  brakeAssistant?: boolean;
  identificationNumber?: string | null;
  licensePlate?: LicensePlate | null;
  mileageKnown?: boolean | null;
  mileage?: string | null;
};

export type LicensePlate = {
  prefix?: string;
  suffix1?: string;
  suffix2?: string;
};

export type CarUsageData = {
  usageKind?: UsageKind;
  annualKilometers?: number;
  parkingPlace?: ParkingPlace;
  seasonPlate?: boolean;
  seasonStartMonth?: string;
  seasonEndMonth?: string;
};

export type FinancingData = {
  financing?: Financing;
  financingProvider?: FinancingProvider;
};

export type PolicyHolder = {
  person?: PersonInformation;
  soleProprietorship?: SoleProprietorshipInformation;
  company?: CompanyInformation;
};

export type PersonInformation = {
  birthday?: string;
  drivingLicenseYear?: number;
  maritalStatus?: string;
  gender?: string | null;
  title?: string;
  firstname?: string;
  surname?: string;
  contacts?: Contacts;
  underAgeChildren?: boolean | null;
  birthdayOldestChild?: string | null;
  address?: Address;
  pointsInFlensburg?: FlensburgPoints;
  homeOwnership?: string;
  occupation?: string;
  numberOfVehicles?: number;
};

export type SoleProprietorshipInformation = {
  branch?: string;
  birthday?: string;
  drivingLicenseYear?: number;
  maritalStatus?: string;
  gender?: string | null;
  title?: string;
  firstname?: string;
  surname?: string;
  contacts?: Contacts;
  underAgeChildren?: boolean | null;
  birthdayOldestChild?: string | null;
  address?: Address;
  pointsInFlensburg?: FlensburgPoints;
  homeOwnership?: string;
  numberOfVehicles?: number;
};

export type CompanyInformation = {
  branch?: string;
  address?: Address;
  contacts?: Contacts;
  companyName?: string;
  numberOfVehicles?: number;
};

export type Address = {
  postcode?: string;
  street?: string;
  houseNumber?: string;
  city?: string;
};

export type Contacts = {
  email?: string;
  phonePrefix?: string;
  phoneNumber?: string;
};

export type PartnerInformation = {
  maritalStatus?: string;
  drivingLicenseYear?: number;
  occupation?: string;
  birthday?: string;
  gender?: string | null;
  title?: string;
  firstname?: string;
  surname?: string;
  address?: Address;
};

export type VehicleOwner = {
  vehicleOwnerKind?: VehicleOwnerKind;
  person?: PersonInformation;
  company?: CompanyInformation;
};

export type DriverData = {
  driversKind?: DriversKind;
  youngDriversExcluded?: boolean;
  birthdayOldestDriver?: string;
  birthdayYoungestDriver?: string;
};

export type PreviousInsuranceData = {
  cancelReason?: CancelReason;
  damagesLastYearFullyComprehensive?: string;
  damagesLastYearLiability?: string;
  ownerKind?: OwnerKind;
  previousInsuranceDuration?: PreviousInsuranceDuration;
  previousInsurer?: string | null;
  previousPolicyNumber?: string | null;
};

export type InsuranceData = {
  insuranceBegin?: string;
  coverageType?: string;
  // Possible values?
  ncdClassLiability?: string;
  ncdClassFullyComprehensive?: string | null;
  deductiblePartiallyComprehensive?: string | null;
  // Type? As far as we know it can only be null
  deductibleFullyComprehensive?: string | null;
  productLine?: string;
  tariffType?: string;
  tariffOptions?: TariffOption[];
  promotionCode?: string | null;
};

export type DrivingExperienceData = {
  drivingExperienceKind?: OwnerDriversKind;
  otherVehicleUsage?: OtherVehicleUsage;
  otherAnnualKilometers?: string;
  otherAnnualDuration?: string;
  otherNumberOfYears?: number;
};

export type OtherVehicleUsage = {
  otherNcdClassLiability?: string;
  otherNcdClassFullyComprehensive?: string;
  otherInsurer?: string;
  otherPolicyNumber?: string;
};

export type Payment = {
  kind?: string;
  frequency?: string;
  iban?: IbanInformation;
  bankAccountOwner?: BankAccountOwner;
};

export type IbanInformation = {
  ibanType?: IbanType;
  iban?: string;
};

export type BankAccountOwner = {
  bankAccountOwnerKind?: OwnerKind;
  person?: PersonInformation;
  company?: CompanyInformation;
};

export type Cover = {
  productLine?: string;
  coverages?: Coverage[];
  optionalCoverages?: Coverage[];
  annualPrice?: string;
  quarterlyPrice?: string;
  halfYearlyPrice?: string;
};

export type Coverage = {
  tariffOption?: TariffOption;
  coverageType?: CoverageType;
  price?: string;
};

export type ExternalReferences = {
  references?: Reference[];
};

export type Reference = {
  key: string;
  value: string;
};

export const InsuranceSituation = {
  FIRST_INSURANCE: 'FIRST_INSURANCE',
  NEW_VEHICLE: 'NEW_VEHICLE',
  INSURER_CHANGE: 'INSURER_CHANGE',
} as const;
export type InsuranceSituation = (typeof InsuranceSituation)[keyof typeof InsuranceSituation];

export const FlensburgPoints = {
  NONE: 'None',
  ONE_OR_MORE: 'OneOrMore',
} as const;
export type FlensburgPoints = (typeof FlensburgPoints)[keyof typeof FlensburgPoints];

export const DriversKind = {
  MISC_DRIVERS: 'MiscDrivers',
  KNOWN_DRIVERS: 'KnownDrivers',
  POLICYHOLDER: 'Policyholder',
  POLICYHOLDER_AND_PARTNER: 'PolicyholderAndPartner',
} as const;
export type DriversKind = (typeof DriversKind)[keyof typeof DriversKind];

export const OwnerDriversKind = {
  MIR: 'Policyholder',
  PARTNER: 'Partner',
  ANDEREN: 'Company',
  CAR_SHARING: 'Carsharing',
  OTHER: 'Other',
};
export type OwnerDriversKind = (typeof OwnerDriversKind)[keyof typeof OwnerDriversKind];

// Possible values?
export const TariffOption = {
  FAS: 'FAS',
  KASKO_CLEVER: 'KaskoClever',
  SCH: 'SCH',
  GAP: 'GAP',
} as const;
export type TariffOption = (typeof TariffOption)[keyof typeof TariffOption];

// Possible values?
export const TariffType = {
  NORMAL: 'Normal',
  PARTNER: 'Partner',
  SECOND_VEHICLE: 'SecondVehicle',
} as const;
export type TariffType = (typeof TariffType)[keyof typeof TariffType];

export const CoverageType = {
  LIABILITY: 'Liability',
  PARTIALLY_COMPREHENSIVE: 'PartiallyComprehensive',
  FULLY_COMPREHENSIVE: 'FullyComprehensive',
} as const;
export type CoverageType = (typeof CoverageType)[keyof typeof CoverageType];

export const CancelReason = {
  CANCELLED_BY_INSURER: 'CancelledByInsurer',
  CANCELLED_BY_POLICYHOLDER: 'CancelledByPolicyholder',
} as const;
export type CancelReason = (typeof CancelReason)[keyof typeof CancelReason];

export const PreviousInsuranceDuration = {
  LONGER_THAN_ONE_YEAR: 'LongerThanOneYear',
  LONGER_THAN_FIVE_YEAR: 'LongerThanFiveYear',
  LONGER_THAN_THREE_YEAR: 'LongerThanThreeYear',
  UP_TO_ONE_YEAR: 'UpToOneYear',
  UNKNOWN: 'Unknown',
} as const;
export type PreviousInsuranceDuration = (typeof PreviousInsuranceDuration)[keyof typeof PreviousInsuranceDuration];

export const OwnerKind = {
  POLICY_HOLDER: 'Policyholder',
  OTHER: 'Other',
} as const;
export type OwnerKind = (typeof OwnerKind)[keyof typeof OwnerKind];

export const VehicleOwnerKind = {
  POLICYHOLDER_MAIN_RESIDENCE: 'PolicyholderMainResidence',
  CHILD_OF_POLICYHOLDER: 'ChildOfPolicyholder',
  PARTNER_OF_POLICYHOLDER: 'PartnerOfPolicyholder',
  OTHER: 'Other',
} as const;
export type VehicleOwnerKind = (typeof VehicleOwnerKind)[keyof typeof VehicleOwnerKind];

export const UsageKind = {
  PRIVATE: 'Private',
  BUSINESS: 'Business',
  BUSINESS_AND_PRIVATE: 'BusinessAndPrivate',
} as const;
export type UsageKind = (typeof UsageKind)[keyof typeof UsageKind];

export const ParkingPlace = {
  STREET: 'Street',
  GARAGE: 'Garage',
  PRIVATE_AREA: 'PrivateOrCompanyProperty',
  PARKHOUSE: 'Parkhouse',
} as const;
export type ParkingPlace = (typeof ParkingPlace)[keyof typeof ParkingPlace];

export const Financing = {
  CREDIT: 'Credit',
  LEASING: 'Leasing',
  NONE: 'None',
} as const;
export type Financing = (typeof Financing)[keyof typeof Financing];

export const IbanType = {
  GERMAN: 'German',
  INTERNATIONAL: 'International',
} as const;
export type IbanType = (typeof IbanType)[keyof typeof IbanType];

export const FinancingProvider = {
  PRINCIPAL_BANK: 'PrincipalBank',
  OTHER: 'Other',
} as const;
export type FinancingProvider = (typeof FinancingProvider)[keyof typeof FinancingProvider];

export const Usecase = {
  Standard: 'Standard',
  Requoting: 'Requoting',
  SelfAdministration: 'SelfAdministration',
  TariffChange: 'TariffChange',
} as const;
export type Usecase = (typeof Usecase)[keyof typeof Usecase];

export const MaritalStatus = {
  SINGLE: 'Single',
  MARRIED: 'Married',
  DIVORCED: 'Divorced',
  WINDOWED: 'Widowed',
  LIVING_COMMUNITY: 'LivingCommunity',
} as const;
export type MaritalStatus = (typeof MaritalStatus)[keyof typeof MaritalStatus];
